import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import type { CraftingRecipe } from '../../../shared/CharacterTypes';
import { useInventory } from './useInventory';
import { shouldRequestRoomData, ROOM_REQUEST_RETRY_MS } from './roomRequestGate';
import {
  readCraftRoom, synchronizeCraftOwner, startCraftLoan, reportCraftAvailability, useCraftLoanSnapshot,
  type CraftResult, type CraftingRoom,
} from './CraftLoanStore';
export type { CraftResult } from './CraftLoanStore';

export interface CraftingState {
  recipes: CraftingRecipe[];
  lastResult: CraftResult | null;
  pendingRecipeId: string | null;
  recipesLoaded: boolean;
  connected: boolean;
}
export interface CraftingActions {
  canCraft: (recipe: CraftingRecipe | null) => boolean;
  refresh: () => void;
  craft: (recipeId: string) => boolean;
}
export function hasAllIngredients(recipe: CraftingRecipe | null, ownedById: ReadonlyMap<string, number>): boolean {
  return Boolean(recipe && recipe.ingredients.every(ingredient => (ownedById.get(ingredient.itemId) ?? 0) >= ingredient.quantity));
}
interface RequestedRoom { room: CraftingRoom; sessionId: string; sentAt: number; answered: boolean }

/** Recipe listeners belong to the view; an actual pending craft belongs to the retained transport loan. */
export function useCrafting(): CraftingState & CraftingActions {
  const [recipes, setRecipes] = useState<CraftingRecipe[]>([]);
  const [recipesLoaded, setRecipesLoaded] = useState(false);
  const [connected, setConnected] = useState(false);
  const bindingRef = useRef<{ room: CraftingRoom; sessionId: string } | null>(null);
  const requestedRoomRef = useRef<RequestedRoom | null>(null);
  const loan = useCraftLoanSnapshot();
  const { items } = useInventory();
  const ownedById = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of items) counts.set(item.id, (counts.get(item.id) ?? 0) + item.quantity);
    return counts;
  }, [items]);

  const refresh = useCallback(() => {
    const room = readCraftRoom();
    if (loan.pendingRecipeId || !room || bindingRef.current?.room !== room
      || bindingRef.current.sessionId !== (room.sessionId ?? '')) return;
    const previous = requestedRoomRef.current;
    setRecipesLoaded(false);
    requestedRoomRef.current = { room, sessionId: room.sessionId ?? '', sentAt: Date.now(), answered: false };
    try { room.send('getRecipes', {}); }
    catch {
      requestedRoomRef.current = previous;
      setRecipesLoaded(recipesLoaded);
      reportCraftAvailability({ success: false, status: 'unavailable', message: 'The recipe list could not be requested. Check your connection.' });
    }
  }, [recipesLoaded, loan.pendingRecipeId]);

  useEffect(() => {
    let room: CraftingRoom | null = null;
    let sessionId = '';
    let blockedRoom: CraftingRoom | null = null;
    let active = true;
    let generation = 0;
    let connectedKey: string | null = null;
    let disposeRecipe: (() => void) | undefined;
    const unbind = () => {
      generation++; disposeRecipe?.(); disposeRecipe = undefined;
      room = null; sessionId = ''; bindingRef.current = null; requestedRoomRef.current = null;
    };
    const request = () => {
      const current = readCraftRoom();
      if (current && current === blockedRoom) return;
      const sid = current?.sessionId ?? '';
      synchronizeCraftOwner();
      if (room !== current || sessionId !== sid) {
        unbind(); setRecipes([]); setRecipesLoaded(false); setConnected(false);
        if (current) {
          room = current; sessionId = sid; bindingRef.current = { room: current, sessionId: sid };
          setConnected(true);
          const epoch = generation;
          const disposer = current.onMessage('recipeList', (message: { recipes?: CraftingRecipe[] }) => {
            if (!active || generation !== epoch || room !== current || readCraftRoom() !== current
              || (current.sessionId ?? '') !== sid) return;
            setRecipes(message.recipes ?? []); setRecipesLoaded(true);
            if (requestedRoomRef.current?.room === current) requestedRoomRef.current.answered = true;
          });
          disposeRecipe = typeof disposer === 'function' ? disposer : undefined;
        }
      }
      if (!current || !shouldRequestRoomData(requestedRoomRef.current, current, sid, Date.now(), ROOM_REQUEST_RETRY_MS)) return;
      requestedRoomRef.current = { room: current, sessionId: sid, sentAt: Date.now(), answered: false };
      try { current.send('getRecipes', {}); } catch { requestedRoomRef.current = null; }
    };
    const onConnect = EventBus.getInstance().on(EVENTS.NET_CONNECTED, data => {
      const key = JSON.stringify([data.roomId, data.roomInstanceId, data.sessionId, data.clientInstanceId]);
      if (connectedKey !== null && connectedKey !== key) unbind();
      connectedKey = key; blockedRoom = null; request();
    });
    const onDisconnect = EventBus.getInstance().on(EVENTS.NET_DISCONNECTED, () => {
      blockedRoom = room; unbind(); setConnected(false); setRecipes([]); setRecipesLoaded(false);
    });
    request();
    const timer = setInterval(request, 500);
    return () => { active = false; onConnect(); onDisconnect(); clearInterval(timer); unbind(); };
  }, []);

  const canCraft = useCallback((recipe: CraftingRecipe | null) => hasAllIngredients(recipe, ownedById), [ownedById]);
  const craft = useCallback((recipeId: string): boolean => {
    const room = readCraftRoom();
    const recipe = recipes.find(candidate => candidate.id === recipeId);
    if (!room || !recipe) {
      reportCraftAvailability({ success: false, status: 'unavailable', recipeId,
        message: !room ? 'Connect to a realm to craft.' : 'Recipe unavailable. Refresh the recipe list.' });
      return false;
    }
    if (bindingRef.current?.room !== room || bindingRef.current.sessionId !== (room.sessionId ?? '')
      || !recipesLoaded || requestedRoomRef.current?.room !== room || requestedRoomRef.current.sessionId !== (room.sessionId ?? '')) {
      reportCraftAvailability({ success: false, status: 'unavailable', recipeId, message: 'Connection changed. Wait for the current recipe list.' });
      return false;
    }
    return startCraftLoan(recipe);
  }, [recipes, recipesLoaded]);
  return { recipes, ...loan, canCraft, refresh, craft, recipesLoaded, connected };
}
